import React from 'react';
import { Box } from '@mui/material';
import { LocationOn, LocalShipping } from '@mui/icons-material';
import { Chip } from 'cprime-foundational-components';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { formatDataValue, getLifecycleDetailsChip } from 'utils/formatFns';
import { generateTestId } from 'utils/general';
import { chipColorMap } from 'utils/colorMappers';
import { getChipSx } from '../utils/utils';
import { isMexicanOrTDPRNonUSADistributor } from 'utils/sectionMappingUtils';
import { getFleetTooltipLabel } from 'utils/dataGetters';

export const CATEGORY_CODE_LABEL = 'Category Code';
export const FLEET_INDICATOR_LABEL = 'Fleet Indicator';
export const LIFECYCLE_STATUS_LABEL = 'Lifecycle Status';

export const mainHeaderFields = {
  brandName: {
    label: 'Brand Name',
    getValue: (data) => data?.main?.header?.divisionName,
    formatValue: (value) => formatDataValue({ value }),
  },
  categoryCode: {
    label: CATEGORY_CODE_LABEL,
    getValue: (data) => data?.main?.header?.lifeCycleStatusCode,
    formatValue: (lifecycleDetails, options = {}) =>
      getLifecycleDetailsChip({
        label: CATEGORY_CODE_LABEL,
        lifecycleDetails,
        isPrint: options.isPrint,
        sectionTitle: 'Vehicle Journey',
        searchContextValues: options.searchContextValues,
      }) || 'N/A',
  },
  categoryDescription: {
    label: 'Category Description',
    getValue: (data) => data?.main?.header?.lifeCycleSubstatusDescription,
    formatValue: (value) =>
      formatDataValue({
        value,
        fallbackString: 'N/A',
      }),
  },
  distributor: {
    label: 'Distributor',
    getValue: (data) => data?.main?.header?.distributorName,
    formatValue: (value) => formatDataValue({ value }),
  },
  fleetIndicator: {
    label: FLEET_INDICATOR_LABEL,
    getValue: (data) => data?.main?.header?.fleetIndicator,
    formatValue: (value) => (value === true ? value : null),
  },
  fleetIndicatorAtSale: {
    label: 'Fleet Indicator At Sale',
    getValue: (data) => data?.main?.header?.fleetIndicator,
    formatValue: (value) => (value === true ? value : null),
  },
  fleetIndicatorChip: {
    label: FLEET_INDICATOR_LABEL,
    getValue: (data) => ({
      fleetIndicator: data?.main?.header?.fleetIndicator,
      distributorCode: data?.main?.header?.distributorCode,
      fdFleet: data?.main?.header?.fdFleet,
    }),
    formatValue: ({ fleetIndicator, distributorCode, fdFleet }, options = {}) => {
      if (options.isVehicleWithLimitedFeatures) {
        return null;
      }

      if (fleetIndicator === null) {
        return null;
      }

      const getFleetChipLabel = () => {
        if (options.isMobile) {
          return '';
        }
        if (fleetIndicator === null) {
          return 'N/A';
        }
        return fleetIndicator ? 'Fleet' : 'Non-fleet';
      };

      const fleetHoverDescription =
        isMexicanOrTDPRNonUSADistributor({ distributorCode }) && getFleetTooltipLabel({ fdFleet });

      return (
        <TooltipWrapper
          tooltipText={
            fleetHoverDescription
              ? `${FLEET_INDICATOR_LABEL} | ${fleetHoverDescription}`
              : FLEET_INDICATOR_LABEL
          }
        >
          <Box display="flex" alignItems="center">
            <Chip
              data-testid={generateTestId('VehicleRecord', 'VehicleDetailsHeader', 'fleet-chip')}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value={getFleetChipLabel()}
                    fieldId="vehicle-hero-fleet-indicator"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={options.isMobile ? <LocalShipping color={chipColorMap.fleetChipText} /> : null}
              color={chipColorMap.fleetChipText}
              backgroundColor={chipColorMap.fleetChipBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  lifeCycleCategoryCode: {
    label: CATEGORY_CODE_LABEL,
    getValue: (data) => data?.main?.header?.lifeCycleStatusCode,
    formatValue: (lifecycleDetails, options = {}) => {
      if (!lifecycleDetails) {
        return null;
      }
      return getLifecycleDetailsChip({
        label: CATEGORY_CODE_LABEL,
        lifecycleDetails,
        isMobile: options.isMobile,
        isPrint: options.isPrint,
        isWithTooltip: true,
        sectionTitle: 'Vehicle Details Header',
        searchContextValues: options.searchContextValues,
      });
    },
  },
  lifecyclePhase: {
    label: 'Lifecycle Phase',
    getValue: (data) => data?.main?.header?.lifeCycleStatus,
    formatValue: (lifecycleDetails, options = {}) =>
      getLifecycleDetailsChip({
        label: 'Lifecycle Phase',
        lifecycleDetails: formatDataValue({ value: lifecycleDetails }),
        isPrint: options.isPrint,
        isWithTooltip: false,
        sectionTitle: 'Vehicle Journey',
        searchContextValues: options.searchContextValues,
      }),
  },
  lifeCycleStatus: {
    label: LIFECYCLE_STATUS_LABEL,
    getValue: (data) => ({
      lifeCycleStatus: data?.main?.header?.lifeCycleStatus,
      statusDesc: data?.main?.header?.lifeCycleSubstatusDescription,
    }),
    formatValue: ({ lifeCycleStatus, statusDesc }, options = {}) => {
      if (!lifeCycleStatus) {
        return null;
      }

      return (
        <TooltipWrapper
          tooltipText={
            statusDesc ? `${LIFECYCLE_STATUS_LABEL} | ${statusDesc}` : LIFECYCLE_STATUS_LABEL
          }
        >
          <Box display="flex" alignItems="center">
            <Chip
              data-testid={generateTestId(
                'VehicleRecord',
                'VehicleDetailsHeader',
                'lifecycle-status-chip',
              )}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value={lifeCycleStatus}
                    fieldId="vehicle-hero-lifecycle-status"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? (
                  <LocationOn color={chipColorMap.lifecycleChipBackground} />
                ) : null
              }
              color={chipColorMap.lifecycleChipText}
              backgroundColor={chipColorMap.lifecycleChipBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  lifeCycleStatusCode: {
    label: 'Lifecycle Status Code',
    getValue: (data) => data?.main?.header?.lifeCycleStatusCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  modelCode: {
    label: 'Model Code',
    getValue: (data) => data?.main?.header?.modelCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  modelPricePhaseCode: {
    label: 'Price Phase Code',
    getValue: (data) => data?.main?.header?.modelPricePhaseCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  modelSerial: {
    label: 'Model | Serial',
    getValue: (data) => ({
      modelCode: data?.main?.header?.modelCode,
      serialNumber: data?.main?.header?.serialNumber,
    }),
    formatValue: ({ modelCode, serialNumber }) => {
      const formattedModel = formatDataValue({ value: modelCode });
      const formattedSerial = formatDataValue({ value: serialNumber });
      return `${formattedModel} | ${formattedSerial}`;
    },
    withCopyIcon: true,
    getCopyValue: (data) => formatDataValue({ value: data?.main?.header?.serialNumber }),
  },
  preProductionVIN: {
    label: 'Pre-Production VIN',
    getValue: (data) => data?.main?.header?.preProductionVIN,
    formatValue: (value) => formatDataValue({ value }),
  },
  salesSeriesName: {
    label: 'Sales Series Name',
    getValue: (data) => data?.main?.header?.salesSeriesName,
    formatValue: (value) => formatDataValue({ value }),
  },
  serialNumber: {
    label: 'Serial Number',
    getValue: (data) => data?.main?.header?.serialNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  urn: {
    label: 'URN',
    getValue: (data) => data?.main?.header?.urn,
    formatValue: (value) => formatDataValue({ value }),
    withCopyIcon: true,
  },
};
